import { useKeptFlag } from "../kept";
import { useMedia } from "../media";
import { t } from "../i18n";
import { Tabs } from "./Tabs";

// A page's two views, its screen and its timeline (byj-1wv.51.3, byj-1wv.51.4):
// a share's and a recording's alike. Screen and Timeline as tabs, or both at
// once one over the other — a split chosen per browser, and offered only
// where the main pane is wide enough for both.

export type View = "screen" | "timeline";

/** Whether the views are split: chosen per browser under `key`, on only where `available` and wide enough. */
export function useSplit(key: string, available: boolean): { splitOn: boolean; wide: boolean; toggle: () => void } {
  const [split, setSplit] = useKeptFlag(key, false);
  // wider than the 900px the panes stack at: a split has room there
  const wide = useMedia("(min-width: 901px)", true);
  return { splitOn: split && wide && available, wide, toggle: () => setSplit(!split) };
}

/** The tabs, or the split's title while split, and Split view right beside them. */
export function ViewBar({ prefix, label, view, onView, screenLabel, split }: {
  /** what the tabs' ids start with (components/Tabs), unique on the page */
  prefix: string;
  label: string;
  view: View;
  onView: (v: View) => void;
  screenLabel: string;
  split: ReturnType<typeof useSplit>;
}) {
  return (
    <div className="view-bar">
      {split.splitOn ? (
        <span className="view-bar-title">{t("session:splitTitle")}</span>
      ) : (
        <Tabs
          prefix={prefix}
          label={label}
          value={view}
          onChange={onView}
          tabs={[
            { id: "screen", label: screenLabel },
            { id: "timeline", label: t("session:tabTimeline") },
          ]}
        />
      )}
      {split.wide && (
        // right beside the tabs it stands for, so it reads as another way to see them
        <button type="button" className="small view-split" aria-pressed={split.splitOn} title={t("session:splitWhat")} onClick={split.toggle}>
          <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
            <rect x="1.5" y="1.5" width="13" height="13" rx="2" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <path d="M1.5 8h13" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          {t("session:split")}
        </button>
      )}
    </div>
  );
}
